import { Injectable, inject, signal } from '@angular/core';
import { DatePipe, formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CommonHelperService {
  private readonly router = inject(Router);
  private readonly datePipe = inject(DatePipe);
  private complexData = new BehaviorSubject<any>([]);

  readonly busy = signal(false);
  readonly lastAction = signal<string | null>(null);

  /**
   * Format a date with DI-provided DatePipe.
   * Ensure DatePipe is provided: either component-level providers or global provideDatePipe().
   */
  formatWithPipe(
    date: Date | string | null | undefined,
    format = 'MM/dd/yyyy'
  ): string {
    return this.datePipe.transform(date, format) ?? '';
  }

  /**
   * Format a date without DI (portable across apps/libs).
   */
  format(
    date: Date | string | null | undefined,
    format = 'MM/dd/yyyy',
    locale = 'en-US'
  ): string {
    return date ? formatDate(date, format, locale) : '';
  }

  /**
   * City, State formatter
   */
  cityState(vendor?: { city?: string; state?: string }): string {
    const city = vendor?.city ?? '';
    const state = vendor?.state ?? '';
    return city && state ? `${city}, ${state}` : city || state;
  }

  /**
   * Safe filename sanitizer
   */
  sanitizeFileName(name: string): string {
    return (
      (name ?? '')
        // Replace invalid characters: < > : " / \ | ? * and all control chars
        .replace(/[<>:"/\\|?*\p{Cc}]/gu, '_') // \p{Cc} = all control characters
        // Collapse multiple spaces into one
        .replace(/\s+/g, ' ')
        // Trim leading/trailing spaces
        .trim()
    );
  }

  /**
   * Unique by id (global)
   */
  uniqueById<T extends { id: number | string }>(list: T[]): T[] {
    const seen = new Set<number | string>();
    return list.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  /**
   * Split by moveType into origin/destination and de-duplicate (optional)
   */
  splitPrograms<
    T extends {
      id: number | string;
      moveType: 'ORIGIN' | 'DESTINATION';
      transloadVendor?: any;
    }
  >(
    programs: T[],
    options?: { dedupe?: boolean; deriveName?: boolean }
  ): { origin: T[]; destination: T[] } {
    const list = options?.dedupe ? this.uniqueById(programs) : programs.slice();

    const origin = list.filter((p) => p.moveType === 'ORIGIN');
    const destination = list.filter((p) => p.moveType === 'DESTINATION');

    if (options?.deriveName) {
      origin.forEach(
        (p) =>
          ((p as any).name = p.transloadVendor
            ? this.cityState(p.transloadVendor)
            : (p as any).name)
      );
      destination.forEach(
        (p) =>
          ((p as any).name = p.transloadVendor
            ? this.cityState(p.transloadVendor)
            : (p as any).name)
      );
    }

    return { origin, destination };
  }

  /**
   * Safe new-tab opening with hash routing under a base path
   */
  openInNewTab(
    relativeHashUrl: string,
    base = `${window.location.origin}/slt/apps/external-web/#`
  ): void {
    const url = `${base}${
      relativeHashUrl.startsWith('/') ? relativeHashUrl : '/' + relativeHashUrl
    }`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  openInNewTabWithParams(
    relativeHashUrl: string,
    queryParams?: Record<string, string>,
    base = `${window.location.origin}/slt/apps/external-web/#`
  ): void {
    let url =
      base +
      (relativeHashUrl.startsWith('/')
        ? relativeHashUrl
        : '/' + relativeHashUrl);

    if (queryParams) {
      const queryString = new URLSearchParams(queryParams).toString();
      url += `?${queryString}`;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Router navigation (same tab)
   */
  go(path: string | any[]): Promise<boolean> {
    this.lastAction.set(`nav:${Array.isArray(path) ? path.join('/') : path}`);
    return Array.isArray(path)
      ? this.router.navigate(path)
      : this.router.navigate([path]);
  }

  buildFileName(fileName: string): string {
    const safeTs =
      this.datePipe.transform(new Date(), 'yyyy-MM-dd_HH-mm-ss') ?? 'timestamp';
    return `${fileName}_${safeTs}.pdf`;
  }

  formatToFixed(value: any, defaultValue = '') {
    return value
      ? `$ ${value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : defaultValue;
  }
  formatAddress(location) {
    if (!location) return '';
    const name: string[] = [
      location.name,
      location.addressLine1,
      location.addressLine2,
      location.city,
      location.state,
      location.country,
      location.externalCloudKey ? `CloudKey#${location.externalCloudKey}` : '',
      location.uopId ? `UPDS#${location.uopId}` : '',
      location.armsAccountNumber ? `ARMS#${location.armsAccountNumber}` : '',
      location.locationFirmNumber ? `FIRM#${location.locationFirmNumber}` : '',
      location.sapVendorNumber ? `SAP#${location.sapVendorNumber}` : '',
      location.locationCode ? `SPS#${location.locationCode}` : '',
      location.businessGroupForAutolookup
        ? `BusGrp#${location.businessGroupForAutolookup}`
        : '',
    ];
    return name.filter(Boolean).join(', ');
  }

  setComplexData(data: any) {
    this.complexData.next(data);
  }

  get complexData$(): any {
    return this.complexData.value;
  }
}
